import BottomSheet from '@components/BottomSheet'
import ChainLogo, { TokenChainAvatar } from '@components/ChainLogo'
import SearchBar from '@components/SearchBar/SearchBar'
import { DEFAULT_L1_CHAINS } from '@plug-core/constants/business'
import useConfig from '@plug-core/hooks/useConfig'
import useEvmChain from '@plug-core/hooks/useEVMChain'
import { PlugToken } from '@plug-core/interfaces/common'
import {
  useCurrentWallet,
  useCustomTokens,
  useUserEVMChains,
  useUserTokens,
} from '@plug-core/state'
import { IconButton, ListGroup, ListRow, Switch, cn } from '@ui'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMiniSquaresPlus } from 'react-icons/hi2'
import { MdDelete } from 'react-icons/md'
import CustomTokenForm from './CustomTokenForm'

interface SelectTokenRowProps {
  token: PlugToken
  toggleSwitch: () => void
  selected: boolean
  custom?: boolean
}

const SelectTokenRow = ({ token, toggleSwitch, selected, custom }: SelectTokenRowProps) => {
  const { t } = useTranslation()

  return (
    <ListRow
      dense
      leading={<TokenChainAvatar logo={token.logo} chains={[token.chain]} size={36} />}
      primary={token.name}
      secondary={token.symbol}
      trailing={
        <span className="flex w-[60px] justify-center">
          {custom ? (
            <IconButton
              aria-label={t('manageTokens.removeToken', { symbol: token.symbol })}
              variant="secondary"
              size="sm"
              onClick={toggleSwitch}
            >
              <MdDelete />
            </IconButton>
          ) : (
            <Switch aria-label={token.name} checked={selected} onCheckedChange={toggleSwitch} />
          )}
        </span>
      }
    />
  )
}

export const ChainTabItem = ({
  chain,
  selected,
  onPress,
}: {
  chain: string
  selected: boolean
  onPress: () => void
}) => {
  const evmChain = useEvmChain(chain)
  const { t } = useTranslation()

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onPress}
      className={cn(
        'mr-1.5 inline-flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-pill border border-solid px-3 font-text text-[13px] font-semibold whitespace-nowrap transition-[background,border-color,transform] duration-150 active:scale-95 motion-reduce:active:scale-100',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        selected
          ? 'border-transparent bg-accent text-on-accent'
          : 'border-hairline bg-surface text-fg hover:bg-surface-2'
      )}
    >
      <ChainLogo chain={chain} size={14} />
      {evmChain?.name || t(`chains.long.${chain}`)}
    </button>
  )
}

export const TokenManager = () => {
  const { toggleToken, isTokenAdded } = useUserTokens()
  const { currentWallet } = useCurrentWallet()
  const { tokens } = useConfig()
  const { t } = useTranslation()
  const { userEVMChains } = useUserEVMChains()
  const { removeToken } = useCustomTokens()

  const [searchTerm, setSearchTerm] = useState('')
  const [bottomSheetVisible, setBottomSheetVisible] = useState(false)
  const [chainMode, setChainMode] = useState<string>('all')
  const [filteredTokens, setFilteredTokens] = useState(tokens)

  const isTokenSelected = (t: PlugToken) => isTokenAdded(currentWallet, t.id)

  const filterTokens = (newSearchTerm: string, newChain: string) => {
    let filtered = tokens

    if (newChain !== 'all') {
      filtered = filtered.filter((t) => t.chain === newChain)
    }

    const matchTerm = newSearchTerm.toLowerCase()
    if (matchTerm) {
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(matchTerm) || t.symbol.toLowerCase().includes(matchTerm)
      )
    }

    setFilteredTokens(filtered)
  }

  const onSearchTermChanged = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
    filterTokens(newSearchTerm, chainMode)
  }

  const onChainChanged = (newChain: string) => {
    setChainMode(newChain)
    filterTokens(searchTerm, newChain)
  }

  return (
    <div className="mb-3 flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={onSearchTermChanged}
            placeholder={t('common.enterTokenName')}
            autoFocus
          />
        </div>

        <IconButton
          aria-label={t('manageTokens.addCustom')}
          variant="secondary"
          size="lg"
          onClick={() => setBottomSheetVisible(true)}
        >
          <HiMiniSquaresPlus />
        </IconButton>
      </div>

      <div className="no-scrollbar flex overflow-x-auto">
        {['all', ...DEFAULT_L1_CHAINS, ...userEVMChains.map(String)].map((chain) => {
          return (
            <ChainTabItem
              chain={chain}
              onPress={() => onChainChanged(chain)}
              selected={chain === chainMode}
              key={chain}
            />
          )
        })}
      </div>

      {filteredTokens.length > 0 && (
        <ListGroup>
          {filteredTokens.map((token) => (
            <SelectTokenRow
              key={token.id}
              token={token}
              toggleSwitch={() => {
                toggleToken(currentWallet, token.id)

                if (token.isCustom) {
                  removeToken(currentWallet, token.id)
                  setFilteredTokens(filteredTokens.filter((t) => t.id !== token.id))
                }
              }}
              selected={isTokenSelected(token)}
              custom={token.isCustom}
            />
          ))}
        </ListGroup>
      )}

      <BottomSheet
        visible={bottomSheetVisible}
        onBackdropClick={() => setBottomSheetVisible(false)}
      >
        <CustomTokenForm hideForm={() => setBottomSheetVisible(false)} />
      </BottomSheet>
    </div>
  )
}
